import React, { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "../components/Dashboard/HomeStyle.css";

import {
  Users,
  Calendar,
  IndianRupee,
  Package,
  Award,
  RefreshCw,
  Download,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  LabelList,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  getPartnerAnalytics,
  getPartnerRevenue,
  getPackageRevenue,
  getPackageData,
  getWhatsAppPackage,
  getWhatsAppPackageRevenue,
  getTotalRevenueGraph,
  getAcquisitionSourceGraph,
  getHostCreatedGraph,
  getSubscriptionStatsApi,
} from "../../services/UserServices/UserService";

const PACKAGE_BAR_COLORS = ["#7c3aed", "#2563eb", "#f59e0b", "#10b981", "#ea580c", "#ec4899", "#800001"];

// Date helper for calculating period date ranges
const getDateRangeForPeriod = (period) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  const formatDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  if (period === "weekly") {
    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    return { startDate: formatDate(start), endDate: formatDate(now) };
  }

  if (period === "monthly") {
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    return { startDate: formatDate(start), endDate: formatDate(end) };
  }

  if (period === "yearly") {
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    return { startDate: formatDate(start), endDate: formatDate(end) };
  }

  return { startDate: "", endDate: "" };
};


const PartnerDashboard = () => {
  const navigate = useNavigate();
  const [partnerAnalytics, setPartnerAnalytics] = useState({});

  // Global Filter State
  const [globalPeriod, setGlobalPeriod] = useState("monthly");
  const initialMonthlyDates = getDateRangeForPeriod("monthly");
  const [startDate, setStartDate] = useState(initialMonthlyDates.startDate);
  const [endDate, setEndDate] = useState(initialMonthlyDates.endDate);

  // Active period key used for indexing fallback chart data
  const activePeriodKey = globalPeriod || "monthly";

  // Handlers for exclusive filter selection
  const handleGlobalPeriodChange = (e) => {
    const selected = e.target.value;
    setGlobalPeriod(selected);
    if (selected) {
      const dates = getDateRangeForPeriod(selected);
      setStartDate(dates.startDate);
      setEndDate(dates.endDate);
    }
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setGlobalPeriod(""); // Clear period dropdown when custom date selected
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setGlobalPeriod(""); // Clear period dropdown when custom date selected
  };

  const handleResetGlobalFilters = () => {
    setGlobalPeriod("monthly");
    const dates = getDateRangeForPeriod("monthly");
    setStartDate(dates.startDate);
    setEndDate(dates.endDate);
  };

  const [partnerRevenueGraph, setPartnerRevenueGraph] = useState([]);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!startDate || !endDate) return;
      try {
        const res = await getPartnerAnalytics(startDate, endDate);
        setPartnerAnalytics(res?.data?.data);

        try {
          const subStatsRes = await getSubscriptionStatsApi(startDate, endDate);
          setSubscriptionStats(subStatsRes?.data?.data || subStatsRes?.data);
        } catch (subErr) {
          console.error("Error fetching subscription stats:", subErr);
        }
      } catch (error) {
        console.error("Error fetching partner analytics:", error);
      }
    };
    fetchAnalytics();
  }, [startDate, endDate]);

  useEffect(() => {
    const getPartnerRevenueFunc = async () => {
      if (!startDate || !endDate) return;
      try {
        const res = await getPartnerRevenue(startDate, endDate);
        console.log("partner revenue res", res);
        const dataObj = res?.data?.data || res?.data;
        const graphArray = dataObj?.graph || dataObj?.data?.graph || (Array.isArray(dataObj) ? dataObj : []);
        setPartnerRevenueGraph(Array.isArray(graphArray) ? graphArray : []);
      } catch (error) {
        console.error("Error fetching partner revenue:", error);
      }
    };
    getPartnerRevenueFunc();
  }, [startDate, endDate]);

  const [packagesList, setPackagesList] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [packageRevenueGraph, setPackageRevenueGraph] = useState([]);
  const [packageRevenuePackages, setPackageRevenuePackages] = useState([]);
  const [packageInfo, setPackageInfo] = useState(null);

  const [whatsAppPackagesList, setWhatsAppPackagesList] = useState([]);
  const [selectedWhatsAppPackageId, setSelectedWhatsAppPackageId] = useState("");
  const [whatsAppRevenueGraph, setWhatsAppRevenueGraph] = useState([]);
  const [whatsAppRevenuePackages, setWhatsAppRevenuePackages] = useState([]);

  useEffect(() => {
    const fetchWaPackages = async () => {
      try {
        const res = await getWhatsAppPackage();
        const resData = res?.data?.data || res?.data;
        const plans = resData?.plans || (Array.isArray(resData) ? resData : []);
        setWhatsAppPackagesList(plans);
      } catch (err) {
        console.error("Error fetching WhatsApp packages:", err);
      }
    };
    fetchWaPackages();
  }, []);

  // Fetch Packages via getPackageData()
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const res = await getPackageData();
        const data = res?.data?.data?.docs || res?.data?.data || res?.data || [];
        const list = Array.isArray(data) ? data : [];
        setPackagesList(list);
      } catch (err) {
        console.error("Error fetching packages via getPackageData:", err);
      }
    };
    fetchPackages();
  }, []);

  // Fetch Package Revenue Graph Data when startDate, endDate or selectedPackageId changes
  useEffect(() => {
    const fetchPackageRevenueData = async () => {
      if (!startDate || !endDate) return;
      try {
        const res = await getPackageRevenue(startDate, endDate, selectedPackageId);
        console.log("package revenue res", res);
        const dataObj = res?.data?.data || res?.data;
        const graphArray = dataObj?.graph || dataObj?.data?.graph || (Array.isArray(dataObj) ? dataObj : []);
        setPackageRevenueGraph(Array.isArray(graphArray) ? graphArray : []);

        if (dataObj?.packages && Array.isArray(dataObj.packages)) {
          setPackageRevenuePackages(dataObj.packages);
        } else {
          setPackageRevenuePackages([]);
        }

        if (dataObj?.package) {
          setPackageInfo(dataObj.package);
        } else {
          setPackageInfo(null);
        }
      } catch (error) {
        console.error("Error fetching package revenue:", error);
      }
    };
    fetchPackageRevenueData();
  }, [startDate, endDate, selectedPackageId]);

  // Fetch WhatsApp Package Revenue Graph Data when startDate, endDate or selectedWhatsAppPackageId changes
  useEffect(() => {
    const fetchWaRevenueData = async () => {
      if (!startDate || !endDate) return;
      try {
        const res = await getWhatsAppPackageRevenue(startDate, endDate, selectedWhatsAppPackageId);
        console.log("whatsapp package revenue res", res);
        const dataObj = res?.data?.data || res?.data;
        const graphArray = dataObj?.graph || dataObj?.data?.graph || (Array.isArray(dataObj) ? dataObj : []);
        setWhatsAppRevenueGraph(Array.isArray(graphArray) ? graphArray : []);

        if (dataObj?.packages && Array.isArray(dataObj.packages)) {
          setWhatsAppRevenuePackages(dataObj.packages);
        } else if (dataObj?.plans && Array.isArray(dataObj.plans)) {
          setWhatsAppRevenuePackages(dataObj.plans);
        } else {
          setWhatsAppRevenuePackages([]);
        }
      } catch (error) {
        console.error("Error fetching WhatsApp package revenue:", error);
      }
    };
    fetchWaRevenueData();
  }, [startDate, endDate, selectedWhatsAppPackageId]);

  // Fetch Total Revenue Graph Data when startDate or endDate changes
  const [hostCreatedGraph, setHostCreatedGraph] = useState(null);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [totalRevenueGraph, setTotalRevenueGraph] = useState([]);

  useEffect(() => {
    const fetchTotalRevenueData = async () => {
      if (!startDate || !endDate) return;
      try {
        const res = await getTotalRevenueGraph(startDate, endDate);
        console.log("total revenue graph res", res);
        const dataObj = res?.data?.data || res?.data;
        const graphArray = dataObj?.graph || dataObj?.data?.graph || (Array.isArray(dataObj) ? dataObj : []);
        setTotalRevenueGraph(Array.isArray(graphArray) ? graphArray : []);
      } catch (error) {
        console.error("Error fetching total revenue graph:", error);
      }
    };
    fetchTotalRevenueData();
  }, [startDate, endDate]);

  // Fetch Vendor Acquisition Source Graph Data when startDate or endDate changes
  const [acquisitionSourceGraph, setAcquisitionSourceGraph] = useState(null);

  useEffect(() => {
    const fetchAcquisitionSourceData = async () => {
      if (!startDate || !endDate) return;
      try {
        const res = await getAcquisitionSourceGraph(startDate, endDate);
        console.log("acquisition source res", res);
        const dataObj = res?.data?.data || res?.data;
        setAcquisitionSourceGraph(dataObj);
      } catch (error) {
        console.error("Error fetching acquisition source graph:", error);
      }
    };
    fetchAcquisitionSourceData();
  }, [startDate, endDate]);

  const activeGrowthChartData = React.useMemo(() => {
    if (!partnerRevenueGraph || partnerRevenueGraph.length === 0) {
      return [];
    }
    return partnerRevenueGraph.map((item) => {
      const periodKey = Object.keys(item).find((k) =>
        ["period", "month", "label", "name", "date", "week", "year", "_id"].includes(k.toLowerCase())
      );
      const interestedVal = item.interested ?? item.interestedPartners ?? item.interestedCount ?? item.count ?? 0;
      const activeVal = item.active ?? item.activePartners ?? item.activeCount ?? item.revenue ?? 0;

      return {
        ...item,
        period: item[periodKey] || item.period || item.month || item.name || "",
        interested: Number(interestedVal) || 0,
        active: Number(activeVal) || 0,
      };
    });
  }, [partnerRevenueGraph]);

  const subscriptionChartData = React.useMemo(() => {
    if (!packageRevenueGraph || packageRevenueGraph.length === 0) {
      return [];
    }
    return packageRevenueGraph.map((item) => {
      const obj = { ...item };
      obj.period = item.period || item.month || item.name || "";
      if (item.revenue !== undefined) obj.revenue = Number(item.revenue) || 0;
      if (item.partners !== undefined) obj.partners = Number(item.partners) || 0;

      let totalStacked = 0;
      Object.keys(item).forEach((k) => {
        if (!["period", "_id", "id", "granularity", "startDate", "endDate", "revenue", "partners", "month", "name", "date", "week", "year", "totalStacked"].includes(k)) {
          totalStacked += Number(item[k]) || 0;
        }
      });
      if (totalStacked === 0 && item.revenue !== undefined) {
        totalStacked = Number(item.revenue) || 0;
      }
      obj.totalStacked = totalStacked;
      return obj;
    });
  }, [packageRevenueGraph]);

  const dynamicPackageNames = React.useMemo(() => {
    const keysSet = new Set();

    if (packageRevenuePackages && packageRevenuePackages.length > 0) {
      packageRevenuePackages.forEach((pkg) => {
        if (pkg.name) keysSet.add(pkg.name);
      });
    }

    if (subscriptionChartData && subscriptionChartData.length > 0) {
      subscriptionChartData.forEach((item) => {
        Object.keys(item).forEach((k) => {
          if (!["period", "_id", "id", "granularity", "startDate", "endDate", "revenue", "partners", "totalStacked"].includes(k)) {
            keysSet.add(k);
          }
        });
      });
    }

    return Array.from(keysSet);
  }, [packageRevenuePackages, subscriptionChartData]);

  const whatsAppChartData = React.useMemo(() => {
    if (!whatsAppRevenueGraph || whatsAppRevenueGraph.length === 0) {
      return [];
    }
    return whatsAppRevenueGraph.map((item) => {
      const obj = { ...item };
      obj.period = item.period || item.month || item.name || "";
      if (item.revenue !== undefined) obj.revenue = Number(item.revenue) || 0;
      if (item.partners !== undefined) obj.partners = Number(item.partners) || 0;

      let totalStacked = 0;
      Object.keys(item).forEach((k) => {
        if (!["period", "_id", "id", "granularity", "startDate", "endDate", "revenue", "partners", "month", "name", "date", "week", "year", "totalStacked"].includes(k)) {
          totalStacked += Number(item[k]) || 0;
        }
      });
      if (totalStacked === 0 && item.revenue !== undefined) {
        totalStacked = Number(item.revenue) || 0;
      }
      obj.totalStacked = totalStacked;
      return obj;
    });
  }, [whatsAppRevenueGraph]);

  const dynamicWaPackageNames = React.useMemo(() => {
    const keysSet = new Set();

    if (whatsAppRevenuePackages && whatsAppRevenuePackages.length > 0) {
      whatsAppRevenuePackages.forEach((pkg) => {
        if (pkg.name) keysSet.add(pkg.name);
      });
    }

    if (whatsAppChartData && whatsAppChartData.length > 0) {
      whatsAppChartData.forEach((item) => {
        Object.keys(item).forEach((k) => {
          if (!["period", "_id", "id", "granularity", "startDate", "endDate", "revenue", "partners", "totalStacked"].includes(k)) {
            keysSet.add(k);
          }
        });
      });
    }

    return Array.from(keysSet);
  }, [whatsAppRevenuePackages, whatsAppChartData]);

  const totalRevenueChartData = React.useMemo(() => {
    if (!totalRevenueGraph || totalRevenueGraph.length === 0) {
      return [];
    }
    return totalRevenueGraph.map((item) => {
      const periodKey = Object.keys(item).find((k) =>
        ["period", "month", "label", "name", "date", "week", "year", "_id"].includes(k.toLowerCase())
      );
      const revVal = item.totalRevenue ?? item.revenue ?? item.total ?? item.amount ?? 0;
      return {
        ...item,
        period: item[periodKey] || item.period || item.month || "",
        totalRevenue: Number(revVal) || 0,
      };
    });
  }, [totalRevenueGraph]);

  const subscriptionStatsPieData = React.useMemo(() => {
    if (!subscriptionStats) return [];

    let statsData = subscriptionStats;
    if (subscriptionStats.graph) {
      statsData = subscriptionStats.graph;
    } else if (subscriptionStats.data) {
      statsData = subscriptionStats.data;
    }

    let pieData = [];
    if (Array.isArray(statsData)) {
      const totals = {};
      statsData.forEach((item) => {
        Object.keys(item).forEach((key) => {
          if (["period", "_id", "id", "granularity", "startDate", "endDate", "total", "renewalRate", "totalPartners"].includes(key)) return;
          totals[key] = (totals[key] || 0) + (Number(item[key]) || 0);
        });
      });
      pieData = Object.keys(totals).map((key) => ({
        name: key.charAt(0).toUpperCase() + key.slice(1),
        value: totals[key],
      }));
    } else if (typeof statsData === 'object') {
      pieData = Object.keys(statsData).filter(k => !["_id", "id", "total", "renewalRate", "totalPartners"].includes(k)).map((key) => ({
        name: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
        value: Number(statsData[key]) || 0,
      }));
    }

    const colors = ["#A60100", "#FFC04C", "#FCDB97"];

    return pieData.map((item, index) => ({
      ...item,
      color: colors[index % colors.length],
    }));
  }, [subscriptionStats]);

  const vendorAcquisitionPieData = React.useMemo(() => {
    if (!acquisitionSourceGraph) {
      return [];
    }

    const graph = acquisitionSourceGraph?.graph || (Array.isArray(acquisitionSourceGraph) ? acquisitionSourceGraph : []);
    const totals = {};

    graph.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (["period", "_id", "id", "granularity", "startDate", "endDate"].includes(key)) return;
        const displayName = key.toLowerCase() === "unknown" ? "Others" : key === "app" ? "App" : key === "website" ? "Website" : key;
        totals[displayName] = (totals[displayName] || 0) + (Number(item[key]) || 0);
      });
    });

    const PALETTE = ["#A60100", "#FFC04C", "#FCDB97"];

    return Object.keys(totals).map((src, idx) => ({
      name: src,
      value: totals[src],
      color: PALETTE[idx % PALETTE.length],
    }));
  }, [acquisitionSourceGraph]);

  const downloadCSV = (filename, headers, rows) => {
    const escapeCell = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val).replace(/"/g, '""');
      return `"${str}"`;
    };
    const headerLine = headers.map(escapeCell).join(",");
    const bodyLines = rows.map((row) => row.map(escapeCell).join(",")).join("\n");
    const csvContent = `${headerLine}\n${bodyLines}`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportGrowthCSV = () => {
    const headers = ["Period", "Interested Partners", "Approved Partners"];
    const rows = (activeGrowthChartData || []).map((item) => [
      item.period || "",
      item.interested ?? 0,
      item.active ?? 0,
    ]);
    downloadCSV("interested_and_approved_partners_growth.csv", headers, rows);
  };

  const exportPackageRevenueCSV = () => {
    let headers = [];
    let rows = [];
    if (selectedPackageId) {
      const selectedPkgObj = (packageRevenuePackages.length > 0 ? packageRevenuePackages : packagesList).find(
        (p) => (p._id || p.id) === selectedPackageId
      );
      const pkgName = selectedPkgObj?.name || selectedPkgObj?.title || "Package";
      headers = ["Period", `${pkgName} Revenue (₹)`, "Partners Count", "Total Revenue (₹)"];
      rows = (subscriptionChartData || []).map((item) => [
        item.period || "",
        item.revenue ?? 0,
        item.partners ?? 0,
        item.totalStacked ?? item.revenue ?? 0,
      ]);
    } else {
      headers = ["Period", ...dynamicPackageNames, "Total Revenue (₹)"];
      rows = (subscriptionChartData || []).map((item) => [
        item.period || "",
        ...dynamicPackageNames.map((pkgName) => item[pkgName] ?? 0),
        item.totalStacked ?? 0,
      ]);
    }
    downloadCSV("partner_packages_revenue.csv", headers, rows);
  };

  const exportWhatsAppRevenueCSV = () => {
    let headers = [];
    let rows = [];
    if (selectedWhatsAppPackageId) {
      const selectedWaPkgObj = (whatsAppRevenuePackages.length > 0 ? whatsAppRevenuePackages : whatsAppPackagesList).find(
        (p) => (p._id || p.id) === selectedWhatsAppPackageId
      );
      const pkgName = selectedWaPkgObj?.name || selectedWaPkgObj?.title || "WhatsApp Package";
      headers = ["Period", `${pkgName} Revenue (₹)`, "Partners Count", "Total Revenue (₹)"];
      rows = (whatsAppChartData || []).map((item) => [
        item.period || "",
        item.revenue ?? 0,
        item.partners ?? 0,
        item.totalStacked ?? item.revenue ?? 0,
      ]);
    } else {
      headers = ["Period", ...dynamicWaPackageNames, "Total Revenue (₹)"];
      rows = (whatsAppChartData || []).map((item) => [
        item.period || "",
        ...dynamicWaPackageNames.map((pkgName) => item[pkgName] ?? 0),
        item.totalStacked ?? 0,
      ]);
    }
    downloadCSV("whatsapp_packages_revenue.csv", headers, rows);
  };

  const exportTotalRevenueCSV = () => {
    const headers = ["Period", "Total Revenue (₹)"];
    const rows = (totalRevenueChartData || []).map((item) => [
      item.period || "",
      item.totalRevenue ?? 0,
    ]);
    downloadCSV("revenue_by_amount.csv", headers, rows);
  };

  const exportTopPartnersCSV = () => {
    const headers = ["Partner Name", "Company Name", "Total Plans", "Revenue (₹)"];
    const rows = (partnerAnalytics?.topPartnerWithRevenue || []).map((partner) => {
      const totalPlans = (partner.partnerPlans || []).reduce(
        (total, data) => total + (data.totalHostLimit || 0),
        0
      );
      return [
        partner.hostName || "",
        partner.companyName || "",
        totalPlans,
        partner.revenue ?? 0,
      ];
    });
    downloadCSV("top_5_partners.csv", headers, rows);
  };

  const exportAcquisitionSourceCSV = () => {
    const headers = ["Acquisition Source", "Vendor Count"];
    const rows = (vendorAcquisitionPieData || []).map((item) => [
      item.name || "",
      item.value ?? 0,
    ]);
    downloadCSV("partner_acquisition_source.csv", headers, rows);
  };

  const exportSubscriptionStatsCSV = () => {
    const headers = ["Subscription Category", "Partner Count"];
    const rows = (subscriptionStatsPieData || []).map((item) => [
      item.name || "",
      item.value ?? 0,
    ]);
    downloadCSV("subscription_stats.csv", headers, rows);
  };

  return (
    <>
      <style>{`
        #main-wrapper, .content-body {
          overflow: visible !important;
        }
        .partner-sticky-filter-bar {
          position: sticky !important;
          top: 80px !important;
          z-index: 100 !important;
          background-color: #ffffff !important;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12) !important;
          border-radius: 1rem !important;
          border: 1.5px solid #F8DBA0 !important;
        }
      `}</style>

      {/* Header Bar matching Home.js */}
      <div className="form-head d-flex mb-3 justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h2 className="mb-0 dshbrd font-fm">Partner Analytics Dashboard</h2>
          <small className="text-muted">Welcome back, Admin! Here is your partner & package performance summary.</small>
        </div>
      </div>

      {/* Sticky Global Filter Bar */}
      <div className="premium-card p-3 mb-4 partner-sticky-filter-bar">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: "36px", height: "36px", backgroundColor: "rgba(255,177,1,0.12)", color: "#800001" }}
            >
              <Calendar size={18} />
            </div>
            <div>
              <h6 className="fw-bold mb-0" style={{ color: "#0f172a", fontSize: "0.9rem" }}>
                Global Date & Timeframe Filter
              </h6>
              <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                Applies globally across all analytics & revenue charts
              </small>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
            {/* Custom Date Filters */}
            <div className="d-flex align-items-center gap-1">
              <label className="small fw-bold text-muted me-1 mb-0" style={{ fontSize: "0.78rem" }}>
                From:
              </label>
              <input
                type="date"
                className="form-control form-control-sm fw-bold"
                value={startDate}
                onChange={handleStartDateChange}
                style={{ height: "34px", borderRadius: "0.5rem", fontSize: "0.8rem", width: "135px" }}
              />
            </div>

            <div className="d-flex align-items-center gap-1">
              <label className="small fw-bold text-muted me-1 mb-0" style={{ fontSize: "0.78rem" }}>
                To:
              </label>
              <input
                type="date"
                className="form-control form-control-sm fw-bold"
                value={endDate}
                onChange={handleEndDateChange}
                style={{ height: "34px", borderRadius: "0.5rem", fontSize: "0.8rem", width: "135px" }}
              />
            </div>

            <span className="badge bg-light text-secondary fw-bold px-2 py-1" style={{ fontSize: "0.7rem" }}>
              OR
            </span>
            <select
              className="form-select form-select-sm fw-bold"
              value={globalPeriod}
              onChange={handleGlobalPeriodChange}
              style={{ height: "34px", borderRadius: "0.5rem", fontSize: "0.8rem", width: "160px", color: "#800001", borderColor: "#e2e8f0" }}
            >
              <option value="">Custom Range</option>
              <option value="weekly">Weekly (Current Week)</option>
              <option value="monthly">Monthly (Current Month)</option>
              <option value="yearly">Yearly (Current Year)</option>
            </select>
            {globalPeriod !== "monthly" && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary fw-bold ms-1"
                onClick={handleResetGlobalFilters}
                style={{ height: "34px", borderRadius: "0.5rem", fontSize: "0.78rem" }}
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4 row-cols-2 row-cols-md-4">
        <div className="col">
          <div className="premium-card p-3 h-100 d-flex align-items-center gap-3">
            <div className="d-flex align-items-center justify-content-center" style={{ minWidth: "48px", height: "48px", borderRadius: "12px", background: "rgba(234,88,12,0.1)", color: "#ea580c" }}>
              <IndianRupee size={22} />
            </div>
            <div>
              <h6 className="stat-label mb-1 text-start">Total Revenue</h6>
              <h4 className="stat-value mb-0 text-start" style={{ color: "#ea580c" }}>
                {partnerAnalytics?.totalRevenue}
              </h4>
            </div>
          </div>

        </div>
        <div className="col">
          <div className="premium-card p-3 h-100 d-flex align-items-center gap-3">
            <div className="d-flex align-items-center justify-content-center" style={{ minWidth: "48px", height: "48px", borderRadius: "12px", background: "rgba(37,99,235,0.1)", color: "#2563eb" }}>
              <Users size={22} />
            </div>
            <div>
              <h6 className="stat-label mb-1 text-start">Approved Partners</h6>
              <h4 className="stat-value mb-0 text-start" style={{ color: "#2563eb" }}>
                {partnerAnalytics?.activePartners}
              </h4>
            </div>
          </div>
        </div>

        <div className="col">
          <div className="premium-card p-3 h-100 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center justify-content-center" style={{ minWidth: "48px", height: "48px", borderRadius: "12px", background: "rgba(16, 185, 129, 0.1)", color: "#10b981" }}>
                <RefreshCw size={22} />
              </div>
              <div>
                <h6 className="stat-label mb-0 text-start">Renewal Rate</h6>
                <div className="d-flex align-items-baseline gap-2 mt-1">
                  <h4 className="stat-value mb-0 text-start" style={{ color: "#10b981" }}>
                    {partnerAnalytics?.renewalRate || "0%"}
                  </h4>
                </div>
                <div className="text-muted mt-1" style={{ fontSize: "0.7rem", fontWeight: "500" }}>
                  <span style={{ color: "#10b981" }}>{partnerAnalytics?.renewedVendors || 0} Renewed</span>
                  <span className="mx-1 text-muted">•</span>
                  <span style={{ color: "#f59e0b" }}>{partnerAnalytics?.expiringVendors || 0} Expiring</span>
                </div>
              </div>
            </div>
          </div>
        </div>        {/* <div className="col">
          <div className="premium-card p-3 h-100 d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                minWidth: "48px",
                height: "48px",
                borderRadius: "12px",
                background: "rgba(124, 58, 237, 0.1)",
                color: "#7c3aed",
              }}
            >
              <Award size={22} />
            </div>
            <div className="overflow-hidden flex-grow-1">
              <h6 className="stat-label mb-1 text-start text-muted small fw-medium">Top Package Revenue</h6>
              <h4 className="stat-value mb-1 text-start fw-bold" style={{ color: "#7c3aed", fontSize: "1.25rem" }}>
                {partnerAnalytics?.topPackageWithRevenue?.revenue ?? "₹0"}
              </h4>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {partnerAnalytics?.topPackageWithRevenue?.name && (
                  <span className="fw-semibold text-dark small text-truncate" style={{ maxWidth: "130px" }} title={partnerAnalytics.topPackageWithRevenue.name}>
                    {partnerAnalytics.topPackageWithRevenue.name}
                  </span>
                )}
                {partnerAnalytics?.topPackageWithRevenue?.partnerCount !== undefined && partnerAnalytics?.topPackageWithRevenue?.partnerCount !== null && (
                  <span className="badge bg-light text-primary border small font-weight-normal px-2 py-1">
                    {partnerAnalytics.topPackageWithRevenue.partnerCount} Partners
                  </span>
                )}
              </div>
            </div>
          </div>
        </div> */}

      </div>


      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="premium-card p-4">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h4 className="chart-title mb-0">1) Interested & Approved Partners Growth</h4>
              <button type="button" className="download-btn" onClick={exportGrowthCSV}>
                <Download size={16} /> Export 
              </button>
            </div>

            {activeGrowthChartData && activeGrowthChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={activeGrowthChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="interested" name="Interested Partners" stroke="#f59e0b" strokeWidth={3} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="active" name="Approved Partners" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center flex-column py-5 my-3 bg-light rounded-3">
                <small className="text-muted fw-medium">No data available for selected period</small>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="premium-card p-4">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div>
                <h4 className="chart-title mb-0">2) Revenue Generated by Partner Packages</h4>
                <small className="text-muted">
                  {packageInfo?.name
                    ? `Package: ${packageInfo.name} ${packageInfo.price ? `(Price: ₹${packageInfo.price})` : ""}`
                    : " Revenue contribution breakdown per partner package & period"}
                </small>
              </div>

              <div className="d-flex gap-2 align-items-center flex-wrap ms-auto">
                <select
                  className="premium-select w-auto"
                  value={selectedPackageId}
                  onChange={(e) => setSelectedPackageId(e.target.value)}
                >
                  <option value="">All Partner Packages</option>
                  {(packageRevenuePackages.length > 0 ? packageRevenuePackages : packagesList).map((pkg) => (
                    <option key={pkg._id || pkg.id} value={pkg._id || pkg.id}>
                      {pkg.name || pkg.title} {pkg.price ? `(₹${pkg.price})` : ""}
                    </option>
                  ))}
                </select>
                <button type="button" className="download-btn" onClick={exportPackageRevenueCSV}>
                  <Download size={16} /> Export 
                </button>
              </div>
            </div>

            {subscriptionChartData && subscriptionChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={subscriptionChartData} barCategoryGap="20%" margin={{ top: 25, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis label={{ value: "Revenue (₹)", angle: -90, position: "insideLeft" }} />
                  <Tooltip
                    formatter={(value, name, item) => {
                      const dataKey = item?.dataKey || name;
                      const isPartnerCount =
                        dataKey === "partners" ||
                        name === "Partners Count" ||
                        (typeof name === "string" && name.toLowerCase().includes("partner"));
                      if (isPartnerCount) {
                        return [Number(value).toLocaleString("en-IN"), "Partners Count"];
                      }
                      return [
                        `₹${Number(value).toLocaleString("en-IN")}`,
                        name === "revenue" ? "Revenue (₹)" : name,
                      ];
                    }}
                  />
                  <Legend />
                  {!selectedPackageId ? (
                    dynamicPackageNames.map((pkgName, idx) => (
                      <Bar
                        key={pkgName}
                        dataKey={pkgName}
                        name={`${pkgName} (₹)`}
                        fill={PACKAGE_BAR_COLORS[idx % PACKAGE_BAR_COLORS.length]}
                        stackId="a"
                        radius={[4, 4, 0, 0]}
                        barSize={40}
                        minPointSize={6}
                      >
                        {idx === dynamicPackageNames.length - 1 && (
                          <LabelList
                            dataKey="totalStacked"
                            position="top"
                            formatter={(value) => (value ? `₹${Number(value).toLocaleString("en-IN")}` : "")}
                            style={{ fill: "#7c3aed", fontWeight: 700, fontSize: "0.8rem" }}
                          />
                        )}
                      </Bar>
                    ))
                  ) : (
                    <>
                      <Bar dataKey="revenue" name="Revenue (₹)" fill="#7c3aed" stackId="a" radius={[0, 0, 0, 0]} barSize={40} minPointSize={6}>
                        <LabelList
                          dataKey="revenue"
                          position="top"
                          formatter={(value) => (value ? `₹${Number(value).toLocaleString("en-IN")}` : "")}
                          style={{ fill: "#7c3aed", fontWeight: 700, fontSize: "0.8rem" }}
                        />
                      </Bar>
                      <Bar dataKey="partners" name="Partners Count" fill="#f59e0b" stackId="a" radius={[4, 4, 0, 0]} barSize={40} minPointSize={6} />
                    </>
                  )}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center flex-column py-5 my-3 bg-light rounded-3">
                <small className="text-muted fw-medium">No data available for selected filter</small>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Revenue Generated by WhatsApp Packages Bar Chart */}
      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="premium-card p-4">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div>
                <h4 className="chart-title mb-0">Revenue Generated by WhatsApp Packages</h4>
                <small className="text-muted">
                  WhatsApp package revenue contribution breakdown per plan & period
                </small>
              </div>

              <div className="d-flex gap-2 align-items-center flex-wrap ms-auto">
                <select
                  className="premium-select w-auto"
                  value={selectedWhatsAppPackageId}
                  onChange={(e) => setSelectedWhatsAppPackageId(e.target.value)}
                >
                  <option value="">All WhatsApp Packages</option>
                  {(whatsAppRevenuePackages.length > 0 ? whatsAppRevenuePackages : whatsAppPackagesList).map((pkg) => (
                    <option key={pkg._id || pkg.id} value={pkg._id || pkg.id}>
                      {pkg.name || pkg.title} {pkg.price ? `(₹${pkg.price})` : ""}
                    </option>
                  ))}
                </select>
                <button type="button" className="download-btn" onClick={exportWhatsAppRevenueCSV}>
                  <Download size={16} /> Export 
                </button>
              </div>
            </div>

            {whatsAppChartData && whatsAppChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={whatsAppChartData} barCategoryGap="20%" margin={{ top: 25, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="period" />
                  <YAxis label={{ value: "Revenue (₹)", angle: -90, position: "insideLeft" }} />
                  <Tooltip
                    formatter={(value, name, item) => {
                      const dataKey = item?.dataKey || name;
                      const isPartnerCount =
                        dataKey === "partners" ||
                        name === "Partners Count" ||
                        (typeof name === "string" && name.toLowerCase().includes("partner"));
                      if (isPartnerCount) {
                        return [Number(value).toLocaleString("en-IN"), "Partners Count"];
                      }
                      return [
                        `₹${Number(value).toLocaleString("en-IN")}`,
                        name === "revenue" ? "WhatsApp Revenue (₹)" : name,
                      ];
                    }}
                  />
                  <Legend />
                  {!selectedWhatsAppPackageId ? (
                    dynamicWaPackageNames.map((pkgName, idx) => (
                      <Bar
                        key={pkgName}
                        dataKey={pkgName}
                        name={`${pkgName} (₹)`}
                        fill={PACKAGE_BAR_COLORS[(idx + 2) % PACKAGE_BAR_COLORS.length]}
                        stackId="a"
                        radius={[4, 4, 0, 0]}
                        barSize={40}
                        minPointSize={6}
                      >
                        {idx === dynamicWaPackageNames.length - 1 && (
                          <LabelList
                            dataKey="totalStacked"
                            position="top"
                            formatter={(value) => (value ? `₹${Number(value).toLocaleString("en-IN")}` : "")}
                            style={{ fill: "#059669", fontWeight: 700, fontSize: "0.8rem" }}
                          />
                        )}
                      </Bar>
                    ))
                  ) : (
                    <>
                      <Bar dataKey="revenue" name="WhatsApp Revenue (₹)" fill="#059669" stackId="a" radius={[0, 0, 0, 0]} barSize={40} minPointSize={6}>
                        <LabelList
                          dataKey="revenue"
                          position="top"
                          formatter={(value) => (value ? `₹${Number(value).toLocaleString("en-IN")}` : "")}
                          style={{ fill: "#059669", fontWeight: 700, fontSize: "0.8rem" }}
                        />
                      </Bar>
                      <Bar dataKey="partners" name="Partners Count" fill="#FFB101" stackId="a" radius={[4, 4, 0, 0]} barSize={40} minPointSize={6} />
                    </>
                  )}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center flex-column py-5 my-3 bg-light rounded-3">
                <small className="text-muted fw-medium">No data available for selected filter</small>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Total Revenue Overview Area Chart */}
      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="premium-card p-4">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div>
                <h4 className="chart-title mb-0"> Revenue By Amount</h4>
                <small className="text-muted">
                  Overall revenue growth combining partner  & WhatsApp packages
                </small>
              </div>
              <button type="button" className="download-btn" onClick={exportTotalRevenueCSV}>
                <Download size={16} /> Export 
              </button>
            </div>

            {totalRevenueChartData && totalRevenueChartData.length > 0 ? (
              <div style={{ width: "100%", height: 350 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={totalRevenueChartData} margin={{ top: 25, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="period" tick={{ fill: "#475569", fontWeight: 600 }} />
                    <YAxis label={{ value: "Total Revenue (₹)", angle: -90, position: "insideLeft" }} tick={{ fill: "#475569", fontWeight: 600 }} />
                    <Tooltip
                      formatter={(value) => [
                        `₹${Number(value).toLocaleString("en-IN")}`,
                        "Total Revenue",
                      ]}
                    />
                    <Legend wrapperStyle={{ paddingTop: "10px", fontWeight: 700 }} />
                    <Bar
                      dataKey="totalRevenue"
                      name="Total Revenue (₹)"
                      fill="#A60000"
                      radius={[4, 4, 0, 0]}
                    >
                      <LabelList
                        dataKey="totalRevenue"
                        position="top"
                        formatter={(value) => (value ? `₹${Number(value).toLocaleString("en-IN")}` : "")}
                        style={{ fill: "#A60000", fontWeight: 700, fontSize: "0.82rem" }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="d-flex align-items-center justify-content-center flex-column py-5 my-3 bg-light rounded-3">
                <small className="text-muted fw-medium">No revenue data available for selected filter</small>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4">




        <div className="col-lg-6 col-12">
          <div className="premium-card p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h4 className="chart-title mb-0">3) Top 5 Partners (Most Bought)</h4>
              <button type="button" className="download-btn" onClick={exportTopPartnersCSV}>
                <Download size={16} /> Export 
              </button>
            </div>
            <div className="table-responsive">
              <Table borderless align="middle" className="mb-0 bg-white">
                <thead>
                  <tr className="border-bottom" style={{ fontSize: "0.75rem", color: "#64748b" }}>
                    <th className="ps-0 bg-white">Partner Name</th>
                    <th className="text-center bg-white">Company Name</th>
                    <th className="text-center bg-white">Total Plan </th>
                    <th className="text-end pe-0 bg-white">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {partnerAnalytics?.topPartnerWithRevenue?.map((partner) => (
                    <tr key={partner.id} className="border-bottom-subtle">
                      <td className="ps-0 cursor-pointer py-2.5 bg-white">
                        <div onClick={() => navigate(`/partner-detail/${partner?._id}`)} className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                            style={{ width: 28, height: 28, backgroundColor: partner.logoColor, fontSize: "0.75rem" }}
                          >
                            {partner.hostName.charAt(0)}
                          </div>
                          <span className="fw-bold text-blue text-truncate" style={{ maxWidth: "150px" }}>
                            {partner.hostName}
                          </span>
                        </div>
                      </td>
                      <td className="text-center py-2.5 bg-white">
                        <span className="text-muted small">{partner.companyName}</span>
                      </td>
                      <td className="text-center fw-bold text-dark py-2.5 bg-white">{partner.partnerPlans?.reduce(
                        (total, data) => total + (data.totalHostLimit || 0),
                        0
                      )}</td>
                      <td className="text-end pe-0 fw-bold text-dark py-2.5 bg-white">₹ {partner.revenue}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

          </div>
        </div>
        <div className="col-lg-6 col-12">
          <div className="premium-card p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div>
                <h4 className="chart-title mb-0">4) Partner Acquisition Source</h4>
                <small className="text-muted">Partner acquisition breakdown by channel & period</small>
              </div>
              <button type="button" className="download-btn" onClick={exportAcquisitionSourceCSV}>
                <Download size={16} /> Export 
              </button>
            </div>

            {vendorAcquisitionPieData && vendorAcquisitionPieData.length > 0 ? (
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={vendorAcquisitionPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={4}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {vendorAcquisitionPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} Vendors`, name]} />
                    <Legend wrapperStyle={{ paddingTop: "10px", fontWeight: 600, fontSize: "0.8rem" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="d-flex align-items-center justify-content-center flex-column py-5 my-3 bg-light rounded-3">
                <small className="text-muted fw-medium">No acquisition data available for selected filter</small>
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-6 col-12">
          <div className="premium-card p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div>
                <h4 className="chart-title mb-0">5) Subscription Stats</h4>
                <small className="text-muted">Subscription distribution among partners</small>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
                {(subscriptionStats?.data?.renewalRate !== undefined || subscriptionStats?.renewalRate !== undefined) && (
                  <div className="px-3 py-1 rounded-pill" style={{ backgroundColor: "rgba(16, 185, 129, 0.1)", color: "#10b981", fontSize: "0.85rem", fontWeight: "600" }}>
                     Renewal Rate: {subscriptionStats?.data?.renewalRate ?? subscriptionStats?.renewalRate}%
                  </div>
                )}
                <button type="button" className="download-btn" onClick={exportSubscriptionStatsCSV}>
                  <Download size={16} /> Export 
                </button>
              </div>
            </div>

            {subscriptionStatsPieData && subscriptionStatsPieData.length > 0 ? (
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={subscriptionStatsPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={4}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {subscriptionStatsPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value}`, name]} />
                    <Legend wrapperStyle={{ paddingTop: "10px", fontWeight: 600, fontSize: "0.8rem" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="d-flex align-items-center justify-content-center flex-column py-5 my-3 bg-light rounded-3">
                <small className="text-muted fw-medium">No subscription stats available for selected filter</small>
              </div>
            )}
          </div>
        </div>
      </div>

    </>
  );
};

export default PartnerDashboard;
